// src/Redux/ratingSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchRatingApi,
  fetchAllRatingsApi,
  insertRatingApi,
  updateRatingApi,
  deleteRatingApi,
} from '../services/LabTestApi/Rating/ratingApi';

// ============================================================
// 1. FETCH RATING FOR SINGLE PRODUCT
// ============================================================
export const fetchRating = createAsyncThunk(
  'rating/fetchRating',
  async ({ productId, categoryId }, { rejectWithValue }) => {
    try {
      const data = await fetchRatingApi(productId, categoryId);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ============================================================
// 2. FETCH RATINGS FOR MULTIPLE PRODUCTS (BULK)
// ============================================================
export const fetchAllRatings = createAsyncThunk(
  'rating/fetchAllRatings',
  async (products, { rejectWithValue }) => {
    try {
      const results = await fetchAllRatingsApi(products);
      return results;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ============================================================
// 3. SUBMIT RATING (Insert or Update)
// ============================================================
export const submitRating = createAsyncThunk(
  'rating/submitRating',
  async ({ ratingId, remark, ratingpoint, userId, categoryId, productId }, { rejectWithValue }) => {
    try {
      const payload = {
        ratingId: ratingId || 0,
        remark: remark || "",
        ratingpoint: Number(ratingpoint),
        userId: Number(userId),
        categoryId: Number(categoryId),
        productId: Number(productId),
      };
      
      let response;
      if (ratingId) {
        response = await updateRatingApi(payload);
      } else {
        response = await insertRatingApi(payload);
      }
      
      return {
        productId,
        categoryId,
        rating: payload,
        response
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ============================================================
// 4. DELETE RATING
// ============================================================
export const deleteRating = createAsyncThunk(
  'rating/deleteRating',
  async ({ ratingId, productId, categoryId }, { rejectWithValue }) => {
    try {
      const response = await deleteRatingApi(ratingId);
      
      return {
        productId,
        categoryId,
        ratingId,
        response
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ============================================================
// 5. INITIAL STATE
// ============================================================
const initialState = {
  ratings: {},      // key: `${productId}_${categoryId}`
  loading: false,
  error: null,
  submitLoading: false,
  submitError: null,
  deleteLoading: false,
  deleteError: null,
};

// ============================================================
// 6. SLICE
// ============================================================
const ratingSlice = createSlice({
  name: 'rating',
  initialState,
  reducers: {
    clearRatings: (state) => {
      state.ratings = {};
      state.error = null;
    },
    clearProductRating: (state, action) => {
      const { productId, categoryId } = action.payload;
      const key = `${productId}_${categoryId}`;
      delete state.ratings[key];
    },
    resetRatingErrors: (state) => {
      state.error = null;
      state.submitError = null;
      state.deleteError = null;
    },
    addRatingManually: (state, action) => {
      const { productId, categoryId, average, total, ratings } = action.payload;
      const key = `${productId}_${categoryId}`;
      state.ratings[key] = { average, total, ratings: ratings || [] };
    },
  },
  extraReducers: (builder) => {
    builder
      // ===== FETCH SINGLE RATING =====
      .addCase(fetchRating.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRating.fulfilled, (state, action) => {
        state.loading = false;
        const { productId, categoryId, average, total, ratings } = action.payload;
        const key = `${productId}_${categoryId}`;
        state.ratings[key] = { average, total, ratings };
      })
      .addCase(fetchRating.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      
      // ===== FETCH ALL RATINGS (BULK) =====
      .addCase(fetchAllRatings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllRatings.fulfilled, (state, action) => {
        state.loading = false;
        action.payload.forEach(({ productId, categoryId, average, total, ratings }) => {
          if (!ratings?.error) {
            const key = `${productId}_${categoryId}`;
            state.ratings[key] = { average, total, ratings: ratings || [] };
          }
        });
      })
      .addCase(fetchAllRatings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      
      // ===== SUBMIT RATING =====
      .addCase(submitRating.pending, (state) => {
        state.submitLoading = true;
        state.submitError = null;
      })
      .addCase(submitRating.fulfilled, (state, action) => {
        state.submitLoading = false;
      })
      .addCase(submitRating.rejected, (state, action) => {
        state.submitLoading = false;
        state.submitError = action.payload || action.error.message;
      })
      
      // ===== DELETE RATING =====
      .addCase(deleteRating.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(deleteRating.fulfilled, (state, action) => {
        state.deleteLoading = false;
        const { productId, categoryId } = action.payload;
        const key = `${productId}_${categoryId}`;
        if (state.ratings[key]) {
          state.ratings[key] = { ...state.ratings[key], average: 0, total: 0, ratings: [] };
        }
      })
      .addCase(deleteRating.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload || action.error.message;
      });
  },
});

export const { 
  clearRatings, 
  clearProductRating, 
  resetRatingErrors,
  addRatingManually 
} = ratingSlice.actions;

export default ratingSlice.reducer;